import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Table, Icon } from 'semantic-ui-react';
import { Link, Loader } from 'nallian-shared-ui/lib/components';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { FEATURE_TABS } from '../../constants';
import RemoveRoleService from '../../services/removeRoleService';
import TypeToConfirmModal from '../../../app/components/TypeToConfirmModal';
import { CursorPointer } from '../../../../portal/app/styles';

const ObsoleteWarning = styled.span`
  color: red;
`;

const FeatureRolesTable = ({
  roles,
  rootUrl,
  appId,
  installationId,
  featureId,
  appInstallationFetch,
  dispatchRemoveRoleDelete,
  removeRoleFetch,
  hideDelete,
}) => {
  const { t } = useTranslation();
  const [removalItem, setRemovalItem] = useState(null);

  const handleCancel = useCallback(() => setRemovalItem(null), []);
  const handleSetRemovalItem = useCallback(role => setRemovalItem(role), []);

  const handleDelete = useCallback(() => {
    if (removalItem) {
      dispatchRemoveRoleDelete(
        appId,
        installationId,
        featureId,
        removalItem.id,
        () => setRemovalItem(null),
        appInstallationFetch.value,
        t
      );
    }
  }, [
    removalItem,
    dispatchRemoveRoleDelete,
    appId,
    installationId,
    featureId,
    appInstallationFetch.value,
    t,
  ]);

  if (!roles.length) {
    return <div>{t('noRolesFound')}</div>;
  }

  return (
    <>
      <Table basic="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width="4">{t('name')}</Table.HeaderCell>
            <Table.HeaderCell width="4">{t('tag')}</Table.HeaderCell>
            <Table.HeaderCell width="4" />
            <Table.HeaderCell width="4">
              <HasRight right="apps/installations/features/roles/delete">
                {!hideDelete && t('actions')}
              </HasRight>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {roles.map(r => (
            <Table.Row key={`role_${r.tag}`}>
              <Table.Cell width="4">
                <Link to={`${rootUrl}/roles/${r.id}/${FEATURE_TABS.DETAILS}`}>{r.name}</Link>
              </Table.Cell>
              <Table.Cell width="4">{r.tag}</Table.Cell>
              <Table.Cell width="4">
                {r.isObsolete && (
                  <ObsoleteWarning>
                    <Icon name="warning sign" color="red" />
                    {t('isObsolete')}
                  </ObsoleteWarning>
                )}
              </Table.Cell>
              <Table.Cell width="4">
                {!hideDelete && (
                  <HasRight right="apps/installations/features/roles/delete">
                    <CursorPointer>
                      {removeRoleFetch?.meta?.roleId === r.id && removeRoleFetch?.pending ? (
                        <Loader inline active size="mini" />
                      ) : (
                        <Icon iconbutton name="trash" onClick={() => handleSetRemovalItem(r)} />
                      )}
                    </CursorPointer>
                  </HasRight>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {removalItem && (
        <TypeToConfirmModal
          header={t('areYouSureDeleteRole', { name: removalItem.name })}
          value={removalItem.name}
          extraCheckSentence={!removalItem.isObsolete ? t('acknowledgeRoleNotObsolete') : null}
          onConfirm={handleDelete}
          onCancel={handleCancel}
          loading={false}
        />
      )}
    </>
  );
};

FeatureRolesTable.propTypes = {
  rootUrl: PropTypes.string.isRequired,
  roles: PropTypes.array,
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  dispatchRemoveRoleDelete: PropTypes.func.isRequired,
  removeRoleFetch: PropTypes.object.isRequired,
  appInstallationFetch: PropTypes.object,
  hideDelete: PropTypes.bool,
};

FeatureRolesTable.defaultProps = {
  roles: [],
  appInstallationFetch: {},
  hideDelete: false,
};

export default RemoveRoleService(FeatureRolesTable);
